import { JsonPipe, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from '@src/app/shared/shared.module';

@Component({
  selector: 'app-input-lab',
  templateUrl: './input-lab.component.html',
  styleUrls: ['./input-lab.component.scss'],
  imports: [SharedModule, FormsModule, ReactiveFormsModule, JsonPipe, NgIf],
})
export class InputLabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private logSequence = 0;
  private previousFormValue: Record<string, unknown> | null = null;

  form: FormGroup;

  ngModelTextValue = '';
  ngModelNumberValue: number | null = 1234.56;
  ngModelCnpjValue = '';
  ngModelMaskValue = '';
  ngModelTextAreaValue = 'Texto inicial do textarea';
  ngModelPasswordValue = '';
  ngModelLoadingValue = 'Carregando...';
  ngModelLoading = true;

  logs: string[] = [];
  scenarioLogs: Record<string, string[]> = {
    'ngModel-text': [],
    'ngModel-number': [],
    'ngModel-cnpj': [],
    'ngModel-mask': [],
    'ngModel-textarea': [],
    'ngModel-password': [],
    'ngModel-loading': [],
    'form-text': [],
    'form-number': [],
    'form-cnpj': [],
    'form-mask': [],
    'form-textarea': [],
    'form-password': [],
  };

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder
  ) {
    this.form = this.fb.group({
      text: ['Valor inicial FormGroup'],
      number: [987.65],
      cnpj: ['12345678000195'],
      mask: ['12345-678'],
      textarea: ['Textarea inicial FormGroup'],
      password: ['senha-inicial'],
    });
  }

  ngOnInit(): void {
    this.log('page:init');
    this.log('scenario:loading:start', { value: this.ngModelLoadingValue });
    this.logToScenario('ngModel-loading', 'scenario:start', { value: this.ngModelLoadingValue });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.log('form:valueChanges', value);
      this.logFormControlChanges(value as Record<string, unknown>);
    });

    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      this.log('form:statusChanges', { status });
    });

    setTimeout(() => {
      this.ngModelLoading = false;
      this.ngModelLoadingValue = 'Finalizado';
      this.log('scenario:loading:finished', { value: this.ngModelLoadingValue });
      this.logToScenario('ngModel-loading', 'scenario:finished', {
        value: this.ngModelLoadingValue,
      });
      this.cdr.detectChanges();
    }, 1800);
  }

  clearLogs(): void {
    this.logs = [];
    this.logSequence = 0;
    this.clearScenarioLogs();
    this.log('logs:cleared');
  }

  resetAllStates(): void {
    this.ngModelTextValue = '';
    this.ngModelNumberValue = 1234.56;
    this.ngModelCnpjValue = '';
    this.ngModelMaskValue = '';
    this.ngModelTextAreaValue = 'Texto inicial do textarea';
    this.ngModelPasswordValue = '';
    this.ngModelLoadingValue = 'Carregando...';
    this.ngModelLoading = true;

    this.form.reset({
      text: 'Valor inicial FormGroup',
      number: 987.65,
      cnpj: '12345678000195',
      mask: '12345-678',
      textarea: 'Textarea inicial FormGroup',
      password: 'senha-inicial',
    });

    this.log('actions:reset-all');

    setTimeout(() => {
      this.ngModelLoading = false;
      this.ngModelLoadingValue = 'Finalizado';
      this.log('scenario:loading:finished-after-reset', { value: this.ngModelLoadingValue });
      this.logToScenario('ngModel-loading', 'scenario:finished-after-reset', {
        value: this.ngModelLoadingValue,
      });
      this.cdr.detectChanges();
    }, 1800);
  }

  onNgModelChange(name: string, value: unknown): void {
    this.log(`ngModelChange:${name}`, value);
    this.logToScenario(name, 'ngModelChange', value);
  }

  onOutput(name: string, value?: unknown): void {
    this.log(`output:${name}`, value);
    const scenario = this.extractScenarioName(name);
    this.logToScenario(scenario, `output:${name}`, value);
  }

  getScenarioLogText(scenario: string): string {
    const scenarioLogs = this.scenarioLogs[scenario] ?? [];
    return scenarioLogs.length > 0 ? scenarioLogs.join('\n') : 'Sem eventos neste cenário.';
  }

  private log(event: string, payload?: unknown): void {
    this.logSequence += 1;
    const prefix = `[${this.logSequence}] ${event}`;
    const suffix = payload === undefined ? '' : ` ${JSON.stringify(payload)}`;
    this.logs = [...this.logs, `${prefix}${suffix}`];
  }

  private clearScenarioLogs(): void {
    Object.keys(this.scenarioLogs).forEach(key => {
      this.scenarioLogs[key] = [];
    });
  }

  private extractScenarioName(eventName: string): string {
    const [scenario] = eventName.split('.');
    return scenario;
  }

  private logToScenario(scenario: string, event: string, payload?: unknown): void {
    if (!this.scenarioLogs[scenario]) {
      this.scenarioLogs[scenario] = [];
    }

    this.logSequence += 1;
    const prefix = `[${this.logSequence}] ${event}`;
    const suffix = payload === undefined ? '' : ` ${JSON.stringify(payload)}`;
    this.scenarioLogs[scenario] = [...this.scenarioLogs[scenario], `${prefix}${suffix}`];
  }

  private logFormControlChanges(currentValue: Record<string, unknown>): void {
    const mapping: Record<string, string> = {
      text: 'form-text',
      number: 'form-number',
      cnpj: 'form-cnpj',
      mask: 'form-mask',
      textarea: 'form-textarea',
      password: 'form-password',
    };

    if (!this.previousFormValue) {
      this.previousFormValue = { ...currentValue };
      return;
    }

    Object.entries(mapping).forEach(([controlName, scenario]) => {
      const current = currentValue[controlName];
      const previous = this.previousFormValue?.[controlName];
      if (JSON.stringify(current) !== JSON.stringify(previous)) {
        this.logToScenario(scenario, `formControl:${controlName}.valueChanges`, current);
      }
    });

    this.previousFormValue = { ...currentValue };
  }
}
